import React, { useEffect, useMemo, useRef, useState } from "react";
import { SupplierEmails } from "./SupplierEmails";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { X, FolderOpen, Folder, Loader2, Search as SearchIcon, FileText, FileSpreadsheet, FileImage, File as FileIcon, ExternalLink, ChevronLeft, ChevronRight, FolderPlus, Upload, Trash2, Home, Pencil } from "lucide-react";
import { isImageFile, imgSrc, ImageLightbox } from "../common/FileViewer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const OFFICE = /\.(docx?|xlsx?|pptx?)$/i;

function prettyBytes(n) {
  if (!n) return "";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
function fileHref(f) {
  const full = `${BACKEND}${f.url}`;
  if (OFFICE.test(f.name || "")) return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(full)}`;
  return full;
}
function FileTypeIcon({ ct, name }) {
  const c = `${ct || ""} ${name || ""}`.toLowerCase();
  if (c.includes("image") || /\.(png|jpe?g|webp|gif|bmp|heic)$/.test(c)) return <FileImage className="h-4 w-4 text-sky-500" />;
  if (c.includes("sheet") || c.includes("excel") || /\.(xlsx?|csv|numbers)$/.test(c)) return <FileSpreadsheet className="h-4 w-4 text-emerald-600" />;
  if (c.includes("pdf") || c.includes("word") || c.includes("text") || /\.(pdf|docx?|txt|rtf)$/.test(c)) return <FileText className="h-4 w-4 text-rose-500" />;
  return <FileIcon className="h-4 w-4 text-slate-400" />;
}

export default function SuppliersFolders({ onClose }) {
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [folderQ, setFolderQ] = useState("");
  const [active, setActive] = useState(null);     // selected top folder obj
  const [subpath, setSubpath] = useState("");      // current sub-folder path within the folder
  const [subfolders, setSubfolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileQ, setFileQ] = useState("");
  const [newName, setNewName] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const fileInputRef = useRef(null);

  const loadFolders = async () => {
    try { setFolders(await api("GET", "/supplier-folders")); }
    catch { toast.error("Could not load supplier folders"); }
    finally { setLoadingFolders(false); }
  };
  useEffect(() => { loadFolders(); }, []);

  const loadLevel = async (f, sp) => {
    const base = f.kind === "supplier"
      ? `kind=supplier&supplier_id=${encodeURIComponent(f.supplier_id)}`
      : `kind=unmatched&folder=${encodeURIComponent(f.folder)}`;
    setLoadingFiles(true);
    try {
      const d = await api("GET", `/supplier-folders/files?${base}&subpath=${encodeURIComponent(sp || "")}`);
      setSubfolders(d.subfolders || []);
      setFiles(d.files || []);
    } catch { toast.error("Could not load files"); }
    finally { setLoadingFiles(false); }
  };

  const openFolder = async (f) => { setActive(f); setSubpath(""); setFileQ(""); setSubfolders([]); setFiles([]); await loadLevel(f, ""); };
  const drill = (seg) => { const sp = subpath ? `${subpath}/${seg}` : seg; setSubpath(sp); loadLevel(active, sp); };
  const goCrumb = (sp) => { setSubpath(sp); loadLevel(active, sp); };

  const createFolder = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const r = await api("POST", "/supplier-folders", { name });
      toast.success(r.existed ? `Opened existing folder "${r.name}"` : `Created folder "${r.name}"`);
      setNewName(""); setShowNew(false);
      await loadFolders();
      await openFolder({ kind: "supplier", supplier_id: r.supplier_id, name: r.name, count: 0 });
    } catch (e) { toast.error(e.message || "Could not create folder"); }
    finally { setBusy(false); }
  };

  const createSubfolder = async () => {
    if (!active || active.kind !== "supplier") return;
    const name = (window.prompt("New sub-folder name:") || "").trim().replace(/\//g, "-");
    if (!name) return;
    const sp = subpath ? `${subpath}/${name}` : name;
    setBusy(true);
    try { await api("POST", "/supplier-folders/subfolder", { supplier_id: active.supplier_id, subpath: sp }); toast.success(`Created sub-folder "${name}"`); await loadLevel(active, subpath); }
    catch (e) { toast.error(e.message || "Could not create sub-folder"); }
    finally { setBusy(false); }
  };

  const uploadDocs = async (fileList) => {
    if (!active || active.kind !== "supplier" || !fileList?.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("subpath", subpath || "");
      try {
        const res = await fetch(`${BACKEND}/api/suppliers/${active.supplier_id}/files`, {
          method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
        });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Upload failed"); }
        ok++;
      } catch (e) { toast.error(`${file.name}: ${e.message}`); }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    setBusy(false);
    await loadLevel(active, subpath); await loadFolders();
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    setBusy(true);
    const path = active.kind === "supplier" ? `/supplier-files/${f.id}` : `/unmatched-files/${f.id}`;
    try { await api("DELETE", path); toast.success("Deleted"); setFiles((prev) => prev.filter((x) => x.id !== f.id)); await loadFolders(); }
    catch (e) { toast.error(e.message || "Could not delete"); }
    finally { setBusy(false); }
  };

  const renameFile = async (f) => {
    const nn = (window.prompt("Rename file to:", f.name) || "").trim();
    if (!nn || nn === f.name) return;
    setBusy(true);
    const path = active.kind === "supplier" ? `/supplier-files/${f.id}` : `/unmatched-files/${f.id}`;
    try { await api("PATCH", path, { name: nn }); toast.success("Renamed"); await loadLevel(active, subpath); }
    catch (e) { toast.error(e.message || "Could not rename"); }
    finally { setBusy(false); }
  };

  const shownFolders = useMemo(() => {
    const ql = folderQ.trim().toLowerCase();
    return (ql ? folders.filter((f) => (f.name || "").toLowerCase().includes(ql)) : folders);
  }, [folders, folderQ]);
  const shownFiles = useMemo(() => {
    const ql = fileQ.trim().toLowerCase();
    return (ql ? files.filter((f) => (f.name || "").toLowerCase().includes(ql)) : files);
  }, [files, fileQ]);
  const totalFiles = useMemo(() => folders.reduce((a, f) => a + (f.count || 0), 0), [folders]);
  const crumbs = useMemo(() => (subpath ? subpath.split("/") : []), [subpath]);
  const isSupplier = active?.kind === "supplier";
  const imageFiles = useMemo(() => shownFiles.filter(isImageFile), [shownFiles]);
  const openImg = (f) => { const idx = imageFiles.findIndex((x) => x.id === f.id); setLightbox({ images: imageFiles, index: Math.max(0, idx) }); };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/55 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6" data-testid="overlay-suppliers-folders">
      <div className="relative w-full max-w-6xl my-2 rounded-lg overflow-hidden shadow-2xl border border-sky-300 bg-gradient-to-b from-sky-100 to-sky-200 flex flex-col h-[calc(100vh-2rem)] sm:h-[calc(100vh-3.5rem)]">
        <div className="flex items-center justify-between bg-[#2a9fd6] text-white px-3 py-1.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold"><FolderOpen className="h-4 w-4" /> Supplier's Folders</div>
          <button onClick={onClose} data-testid="suppliers-folders-close-x" title="Close" className="h-6 w-6 rounded-sm hover:bg-white/20 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 flex-1 min-h-0 flex flex-col overflow-y-auto lg:overflow-hidden">
          <div className="flex items-center gap-3 mb-3 shrink-0">
            <div className="h-10 w-10 rounded-full bg-white shadow flex items-center justify-center shrink-0"><FolderOpen className="h-5 w-5 text-sky-500" /></div>
            <div>
              <h2 className="text-xl font-display font-bold text-slate-700 leading-tight">Supplier's Folders</h2>
              <p className="text-[11px] text-slate-500">{folders.length} folders · {totalFiles.toLocaleString()} files from the Product Suppliers library</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] lg:grid-rows-[minmax(0,1fr)] gap-4 lg:flex-1 lg:min-h-0">
            {/* folder list */}
            <div className="rounded-sm border border-slate-300 bg-white/80 flex flex-col min-h-0">
              <div className="p-2 border-b border-slate-200 space-y-2 shrink-0">
                <div className="relative">
                  <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input value={folderQ} onChange={(e) => setFolderQ(e.target.value)} placeholder="Find a supplier folder…" data-testid="sf-folder-search"
                    className="w-full rounded-sm border border-slate-300 bg-white pl-8 pr-2 py-1.5 text-[13px]" />
                </div>
                {showNew ? (
                  <div className="flex items-center gap-1">
                    <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()}
                      placeholder="New folder name…" data-testid="sf-new-folder-input"
                      className="flex-1 min-w-0 rounded-sm border border-sky-300 bg-white px-2 py-1.5 text-[13px]" />
                    <button onClick={createFolder} disabled={busy} data-testid="sf-new-folder-save" className="rounded-sm bg-sky-600 hover:bg-sky-700 text-white text-[12px] font-semibold px-2.5 py-1.5 disabled:opacity-50">Add</button>
                    <button onClick={() => { setShowNew(false); setNewName(""); }} className="rounded-sm border border-slate-300 bg-white text-slate-600 px-2 py-1.5"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <button onClick={() => setShowNew(true)} data-testid="sf-new-folder-btn"
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-sm border border-dashed border-sky-400 bg-sky-50 text-sky-700 text-[12px] font-semibold py-1.5 hover:bg-sky-100"><FolderPlus className="h-4 w-4" /> New Folder</button>
                )}
              </div>
              <div className="max-h-[52vh] lg:max-h-none lg:flex-1 min-h-0 overflow-y-auto divide-y divide-slate-100" data-testid="sf-folder-scroll">
                {loadingFolders ? (
                  <div className="h-40 flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading folders…</div>
                ) : shownFolders.map((f) => (
                  <button key={f.kind === "supplier" ? f.supplier_id : `um-${f.folder}`} data-testid={`sf-folder-${f.name}`} onClick={() => openFolder(f)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-sky-50 ${active && active.name === f.name ? "bg-sky-100" : ""}`}>
                    <Folder className={`h-4 w-4 shrink-0 ${f.kind === "unmatched" ? "text-amber-500" : "text-sky-500"}`} />
                    <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-slate-700">{f.name}</span>
                    {f.kind === "unmatched" && <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-100 rounded px-1">unfiled</span>}
                    <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{f.count}</span>
                  </button>
                ))}
                {!loadingFolders && shownFolders.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No folders match.</div>}
              </div>
            </div>

            {/* files / sub-folders */}
            <div className="relative rounded-sm border border-slate-300 bg-white/80 min-h-[20rem] lg:min-h-0 flex flex-col">
              {active && showMail && isSupplier && <div className="absolute inset-x-0 bottom-0 top-[49px] z-10 bg-white flex flex-col"><SupplierEmails supplierId={active.supplier_id} /></div>}
              {!active ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-8">
                  <FolderOpen className="h-10 w-10 mb-2 text-slate-300" />
                  Select a supplier folder on the left to browse its files.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-2 border-b border-slate-200 flex-wrap shrink-0" data-testid="sf-toolbar">
                    <button onClick={() => setActive(null)} className="lg:hidden rounded-sm border border-slate-300 bg-white p-1"><ChevronLeft className="h-4 w-4" /></button>
                    {/* breadcrumb */}
                    <div className="flex items-center gap-1 flex-1 min-w-0 text-[13px] font-bold text-slate-800" data-testid="sf-breadcrumb">
                      <button onClick={() => goCrumb("")} className="inline-flex items-center gap-1 hover:text-sky-600 truncate"><Home className="h-3.5 w-3.5" /> {active.name}</button>
                      {crumbs.map((c, i) => (
                        <span key={i} className="inline-flex items-center gap-1 min-w-0">
                          <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <button onClick={() => goCrumb(crumbs.slice(0, i + 1).join("/"))} className="hover:text-sky-600 truncate">{c}</button>
                        </span>
                      ))}
                    </div>
                    {isSupplier && (
                      <>
                        <button onClick={() => setShowMail((x) => !x)} data-testid="sf-emails-btn"
                          className={`inline-flex items-center gap-1 rounded-sm text-[12px] font-semibold px-2.5 py-1.5 ${showMail ? "bg-[#123a63] text-white" : "border border-[#123a63] text-[#123a63] bg-white hover:bg-slate-50"}`}>{showMail ? "Files" : "Emails"}</button>
                        <button onClick={createSubfolder} disabled={busy} data-testid="sf-new-subfolder-btn"
                          className="inline-flex items-center gap-1 rounded-sm border border-sky-400 bg-sky-50 text-sky-700 text-[12px] font-semibold px-2 py-1.5 hover:bg-sky-100 disabled:opacity-50"><FolderPlus className="h-3.5 w-3.5" /> Sub-folder</button>
                        <input ref={fileInputRef} type="file" multiple className="hidden" data-testid="sf-upload-input"
                          onChange={(e) => { uploadDocs(e.target.files); e.target.value = ""; }} />
                        <button onClick={() => fileInputRef.current?.click()} disabled={busy} data-testid="sf-upload-btn"
                          className="inline-flex items-center gap-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold px-2.5 py-1.5 disabled:opacity-50">
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload</button>
                      </>
                    )}
                    <div className="relative">
                      <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <input value={fileQ} onChange={(e) => setFileQ(e.target.value)} placeholder="Filter…" data-testid="sf-file-search"
                        className="w-32 rounded-sm border border-slate-300 bg-white pl-7 pr-2 py-1 text-[12px]" />
                    </div>
                  </div>

                  <div className="relative flex-1 min-h-0 max-h-[60vh] lg:max-h-none overflow-y-auto" data-testid="sf-file-scroll"
                    onDragOver={(e) => { if (!isSupplier) return; e.preventDefault(); if (!dragOver) setDragOver(true); }}
                    onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
                    onDrop={(e) => { if (!isSupplier) return; e.preventDefault(); setDragOver(false); uploadDocs(e.dataTransfer.files); }}>
                    {isSupplier && dragOver && <div className="absolute inset-0 z-20 bg-sky-500/10 border-2 border-dashed border-sky-500 rounded-sm flex items-center justify-center pointer-events-none" data-testid="sf-dropzone"><span className="text-sky-700 font-black text-sm bg-white/95 px-4 py-2 rounded-lg shadow">Drop file or image to upload</span></div>}
                    {loadingFiles ? (
                      <div className="h-40 flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {/* sub-folders first */}
                        {subfolders.map((sf) => (
                          <li key={`d-${sf.name}`} data-testid={`sf-subfolder-${sf.name}`}>
                            <button onClick={() => drill(sf.name)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-sky-50">
                              <Folder className="h-4 w-4 text-amber-500 shrink-0" />
                              <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-slate-800">{sf.name}</span>
                              <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{sf.count}</span>
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            </button>
                          </li>
                        ))}
                        {/* files */}
                        {shownFiles.map((f) => (
                          <li key={f.id} data-testid={`sf-file-${f.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                            {isImageFile(f)
                              ? <button onClick={() => openImg(f)} data-testid={`sf-thumb-${f.id}`} className="shrink-0 rounded overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-sky-300"><img src={imgSrc(f)} alt={f.name} loading="lazy" className="h-14 w-14 object-cover" /></button>
                              : <FileTypeIcon ct={f.content_type} name={f.name} />}
                            <button onClick={() => (isImageFile(f) ? openImg(f) : window.open(fileHref(f), "_blank"))} className="flex-1 min-w-0 truncate text-left text-[13px] text-slate-800 hover:text-sky-700" title={f.name}>{f.name}</button>
                            <span className="text-[11px] text-slate-400 shrink-0">{prettyBytes(f.size)}</span>
                            {isImageFile(f)
                              ? <button onClick={() => openImg(f)} data-testid={`sf-open-${f.id}`} className="inline-flex items-center gap-1 rounded-sm border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-50 shrink-0">View <ExternalLink className="h-3 w-3" /></button>
                              : <a href={fileHref(f)} target="_blank" rel="noreferrer" data-testid={`sf-open-${f.id}`} className="inline-flex items-center gap-1 rounded-sm border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-50 shrink-0">Open <ExternalLink className="h-3 w-3" /></a>}
                            {isSupplier && <button onClick={() => renameFile(f)} disabled={busy} data-testid={`sf-rename-${f.id}`} title="Rename"
                              className="rounded-sm border border-slate-300 bg-white p-1 text-slate-600 hover:bg-slate-50 shrink-0 disabled:opacity-50"><Pencil className="h-3.5 w-3.5" /></button>}
                            <button onClick={() => deleteFile(f)} disabled={busy} data-testid={`sf-delete-${f.id}`} title="Delete"
                              className="rounded-sm border border-slate-300 bg-white p-1 text-rose-600 hover:bg-rose-50 shrink-0 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /></button>
                          </li>
                        ))}
                        {subfolders.length === 0 && shownFiles.length === 0 && <li className="p-8 text-center text-slate-400 text-sm">This folder is empty.</li>}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <button onClick={onClose} data-testid="suppliers-folders-close" className="mt-4 w-full rounded-md bg-slate-900 hover:bg-slate-700 text-white font-bold py-2.5">Close</button>
        </div>
      </div>
      {lightbox && <ImageLightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}
